import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Camera, Upload, Loader2, CheckCircle2, AlertCircle, RefreshCw, FileText } from 'lucide-react';

// Ridimensiona un'immagine lato client per ridurre il peso prima dell'invio.
// I PDF vengono inviati cosi' come sono.
const fileToPayload = (file) => new Promise((resolve, reject) => {
  if (file.type === 'application/pdf') {
    const reader = new FileReader();
    reader.onload = () => resolve({ file_base64: String(reader.result), mime_type: 'application/pdf' });
    reader.onerror = reject;
    reader.readAsDataURL(file);
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1800;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const r = Math.min(MAX / width, MAX / height);
        width = Math.round(width * r);
        height = Math.round(height * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve({ file_base64: canvas.toDataURL('image/jpeg', 0.72), mime_type: 'image/jpeg' });
    };
    img.onerror = reject;
    img.src = String(reader.result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function CapturaMenuPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState('loading'); // loading | ready | invalid | sending | done | error
  const [restaurant, setRestaurant] = useState('');
  const [preview, setPreview] = useState(null);
  const [payload, setPayload] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    api.get(`/public/menu-capture/${token}`)
      .then(({ data }) => {
        setRestaurant(data.restaurant_name || '');
        setState(data.valid ? 'ready' : 'invalid');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const p = await fileToPayload(file);
      setPayload(p);
      setPreview(p.mime_type === 'application/pdf' ? 'pdf' : p.file_base64);
    } catch {
      setErrorMsg(t('capture.readError'));
      setState('error');
    }
  };

  const send = async () => {
    if (!payload) return;
    setState('sending');
    try {
      await api.post(`/public/menu-capture/${token}`, payload);
      setState('done');
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || t('capture.sendError'));
      setState('error');
    }
  };

  const reset = () => { setPreview(null); setPayload(null); setErrorMsg(''); setState('ready'); };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-8" data-testid="capture-menu-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="font-display font-black text-2xl tracking-tight text-primary">Vexel POS</div>
          <div className="text-sm text-muted-foreground mt-1">{t('capture.heading')}</div>
          {restaurant && <div className="text-xs text-muted-foreground mt-0.5">{restaurant}</div>}
        </div>

        {state === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-sm">{t('capture.checking')}</span>
          </div>
        )}

        {state === 'invalid' && (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-center" data-testid="capture-invalid">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
            <div className="font-semibold mb-1">{t('capture.invalidTitle')}</div>
            <div className="text-sm text-muted-foreground">{t('capture.invalidHint')}</div>
          </div>
        )}

        {state === 'done' && (
          <div className="rounded-2xl border border-pos-free/40 bg-pos-free/5 p-6 text-center" data-testid="capture-done">
            <CheckCircle2 className="h-12 w-12 text-pos-free mx-auto mb-3" />
            <div className="font-semibold text-lg mb-1">{t('capture.doneTitle')}</div>
            <div className="text-sm text-muted-foreground mb-4">{t('capture.doneHint')}</div>
            <Button variant="outline" className="rounded-xl" onClick={reset} data-testid="capture-send-another">
              <RefreshCw className="h-4 w-4 mr-2" /> {t('capture.sendAnother')}
            </Button>
          </div>
        )}

        {(state === 'ready' || state === 'sending' || state === 'error') && (
          <div className="space-y-4">
            {preview ? (
              <div className="rounded-2xl border border-border overflow-hidden bg-muted/40 flex items-center justify-center min-h-[220px]" data-testid="capture-preview">
                {preview === 'pdf' ? (
                  <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
                    <FileText className="h-12 w-12" />
                    <span className="text-sm font-medium">{t('capture.pdfReady')}</span>
                  </div>
                ) : (
                  <img src={preview} alt="Menu" className="w-full max-h-[380px] object-contain" />
                )}
              </div>
            ) : (
              <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">
                <Camera className="h-10 w-10 mx-auto mb-2 opacity-70" />
                <div className="text-sm">{t('capture.instructions')}</div>
              </div>
            )}

            {errorMsg && (
              <div className="rounded-xl bg-destructive/10 text-destructive text-sm px-3 py-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" /> {errorMsg}
              </div>
            )}

            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="capture-camera-input" />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} data-testid="capture-file-input" />

            {!preview ? (
              <div className="grid grid-cols-2 gap-3">
                <Button className="rounded-xl h-12" onClick={() => cameraRef.current?.click()} data-testid="capture-open-camera">
                  <Camera className="h-4 w-4 mr-2" /> {t('capture.camera')}
                </Button>
                <Button variant="outline" className="rounded-xl h-12" onClick={() => fileRef.current?.click()} data-testid="capture-open-file">
                  <Upload className="h-4 w-4 mr-2" /> {t('capture.uploadFile')}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="rounded-xl h-12" onClick={reset} disabled={state === 'sending'}>
                  {t('capture.retake')}
                </Button>
                <Button className="rounded-xl h-12" onClick={send} disabled={state === 'sending'} data-testid="capture-send">
                  {state === 'sending' ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('capture.sending')}</> : t('capture.send')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
